import React, { useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CheckCircle, CreditCard, AlertCircle, Lock } from 'lucide-react';

// The server—not this component—determines the authorized invoice amount.
export default function FinalPaymentForm({ jobId, expectedAmountCents, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  async function handlePay(event) {
    event.preventDefault();
    if (busy) return;
    if (!stripe || !elements || !jobId) {
      setError('Payment is not ready. Please reload the page.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await httpsCallable(getFunctions(), 'createFinalPaymentIntent')({ jobId });
      const { clientSecret, amountCents } = result.data || {};
      if (!clientSecret || !Number.isSafeInteger(amountCents) || amountCents < 50 ||
          amountCents !== expectedAmountCents) {
        throw new Error('The balance changed. Refresh the page or contact dispatch before paying.');
      }
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (stripeError) throw stripeError;
      if (paymentIntent?.status !== 'succeeded') {
        throw new Error('Payment is not yet complete. Check its status before trying again.');
      }
      setSubmitted(true);
      onSuccess?.();
    } catch (err) {
      setError(err?.message || 'Payment could not be processed.');
    } finally {
      setBusy(false);
    }
  }
  if (submitted) return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-5 text-green-900">
      <div className="font-semibold flex items-center gap-2"><CheckCircle size={19}/> Card payment submitted</div>
      <p className="text-sm mt-2">Your work order will show Paid in Full after Stripe's secure webhook verifies the payment. Do not pay again while verification is pending.</p>
    </div>
  );
  return (
    <form onSubmit={handlePay} className="space-y-4">
      <div className="flex gap-2 items-center text-lg font-semibold"><CreditCard size={20}/> Pay final balance</div>
      <p className="text-2xl font-bold">${(expectedAmountCents / 100).toFixed(2)}</p>
      <p className="text-sm text-gray-600">This is the remaining 50% of your accepted quote. Your deposit was already received.</p>
      <label className="block text-sm font-medium">Card details
        <div className="p-3 mt-2 border border-gray-300 rounded-lg">
          <CardElement options={{ style: { base: { fontSize: '16px' } } }}/>
        </div>
      </label>
      {error && <p role="alert" className="text-sm rounded-lg p-3 bg-red-50 text-red-800 flex gap-2"><AlertCircle size={18}/>{error}</p>}
      <button disabled={!stripe || busy} className="w-full rounded-lg py-3 bg-blue-700 text-white font-semibold disabled:opacity-50 flex justify-center gap-2" type="submit">
        <Lock size={17}/>{busy ? 'Processing…' : `Pay $${(expectedAmountCents / 100).toFixed(2)} securely`}
      </button>
      <p className="text-xs text-gray-500">Payment processed by Stripe. Your card is not automatically charged when you approve the service.</p>
    </form>
  );
}
